import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { LogoUpload } from '@/components/LogoUpload';
import { PartnerModalFormData, partnerModalFormSchema } from './schemas';
import { usePutPartnerMutation } from '@/services/queries/partner';
import { UpdatePartnerPayload } from '@/services/request/partner';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { Partner } from '@/model';
import { useTranslation } from 'react-i18next';

export interface PartnerEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  partner: Partner;
  onClose: () => void;
  onDone?: () => void;
}

export const PartnerEditModal = ({ isOpen, onClose, partner, onDone }: PartnerEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const updatePartnerByIdMutation = usePutPartnerMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<PartnerModalFormData>({
    defaultValues: { name: partner.name, file: undefined },
    resolver: zodResolver(partnerModalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: PartnerModalFormData) => {
      if (!partner) {
        return;
      }

      const payload: UpdatePartnerPayload = {
        logo: data.file,
        name: data.name,
      };

      updatePartnerByIdMutation.mutate(
        {
          id: partner.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_error'),
            });
          },
        },
      );
    },
    [onDone, partner, toast, updatePartnerByIdMutation, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_title')}</ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_name_label')}
              {...register('name')}
              helperText={t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_name_helper')}
              maxLength={30}
              rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <Text fontWeight='bold' mb={2}>
                {t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_image_label')}
              </Text>

              <LogoUpload
                imageURL={partner.url}
                onFileChange={file => setValue('file', file)}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_cancel_button')}
            </Button>
            <Button onClick={handleConfirm}>{t('pages.partners.partners_section.components.partner_edit_modal.partner_edit_save_button')}</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={updatePartnerByIdMutation.isLoading} />
    </Modal>
  );
};
